import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../../hooks/useJobs";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import type { NurseFullRole } from "../../../../utils/models";
import { isOpenShiftDurationAllowed } from "../../../../utils/openShiftDuration";
import { getRolesOfRoleGroups } from "../../../../utils/roles";
import { usesJobSelection } from "../../../UIToolkit/molecules/RoleGroupOrJobSelector";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";
import { type GapsPostPrefill, type GapsPostTimes, POST_QUANTITY_MIN, postPrefillFor } from "./gapsPostShiftPrefill";

/** The two incentives a post can carry here (P3-9): none, or a flat amount per person. */
export enum GapsPostIncentive {
	None = "none",
	Flat = "flat",
}

/** The classic post screen's cap on a flat incentive. */
export const POST_BONUS_MAX = 1000;

/**
 * The incentive an incentive field says — the flat amount typed, or none — and whether it can be written: none, or a
 * flat amount above zero and at most `POST_BONUS_MAX`. The post sheet and the posted sheet's incentive step both read
 * their field through it, so the two cannot disagree on what a valid amount is.
 */
export const postIncentiveOf = (incentive: GapsPostIncentive, bonusText: string): { bonus: number; isBonusValid: boolean } => {
	const bonus = incentive === GapsPostIncentive.Flat ? Number.parseFloat(bonusText) : 0;
	return { bonus, isBonusValid: incentive === GapsPostIncentive.None || (Number.isFinite(bonus) && bonus > 0 && bonus <= POST_BONUS_MAX) };
};

/** Two role-group picks name the same groups, in any order. */
const isSameSelection = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((name) => b.includes(name));

/**
 * Whether the form still posts for the group it was prefilled from: its job (or role groups) and its window — the
 * prefill's window moved to the form's day (`prefillWindow`), so a Date move alone keeps it. Once the user moves the
 * job or the hours, the group's own numbers no longer describe this post — the sheet stops comparing against them.
 */
const isStillOnPrefill = (
	prefill: GapsPostPrefill | null,
	prefillWindow: GapsPostTimes | undefined,
	fields: { postsByJob: boolean; jobId: string; initialJobId: string; roleGroups: string[]; shiftStart: DateTime; shiftEnd: DateTime },
): boolean =>
	prefill !== null &&
	prefillWindow !== undefined &&
	(fields.postsByJob ? fields.jobId === fields.initialJobId : isSameSelection(fields.roleGroups, prefill.roleGroups)) &&
	fields.shiftStart.toMillis() === prefillWindow.startTime.toMillis() &&
	fields.shiftEnd.toMillis() === prefillWindow.endTime.toMillis();

/**
 * The post sheet's fields (P3-9): the classic post screen's, prefilled from the group the sheet was opened for.
 *
 * Every field starts from the group in INITIAL state, never an effect: the time picker's prefill holds only until the
 * role or location changes, so seeding them afterwards would count as a change and drop the group's hours. The prefill
 * is read once: later changes to the group (a refresh, the post itself) must not reset what the user is editing.
 *
 * `fields` is what the sheet draws and edits; `prefill` and `isComplete` are the write's (`useGapsPostShift`), kept
 * apart so the sheet reads the write-gated `canPost`, never the ungated `isComplete`.
 */
export const useGapsPostShiftForm = ({ group, date }: { group: GapsScopedShiftGroup | undefined; date: DateTime }) => {
	const { chosenDepartment } = useHospitalData();
	const enabledJobs = useJobs();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const postsByJob = usesJobSelection(jobManagementEnabled, enabledJobs);
	const [prefill] = useState<GapsPostPrefill | null>(() => (group === undefined ? null : postPrefillFor(group, date, chosenDepartment)));
	// DEFAULT: "" is the pickers' own "nothing chosen" — an unscoped post, or a role section (which posts by role group)
	// starts with no job, exactly as the classic screen does; the Post button stays off until one is picked.
	const [initialJobId] = useState<string>(prefill === null || prefill.jobId === null ? "" : prefill.jobId);
	const [jobId, setJobId] = useState<string>(initialJobId);
	const [roleGroups, setRoleGroups] = useState<string[]>(prefill === null ? [] : prefill.roleGroups);
	// DEFAULT: an unscoped post (the bottom bar's Post) has no group to count from, and one is the least it can ask for.
	const [neededNurses, setNeededNurses] = useState<number>(prefill === null ? POST_QUANTITY_MIN : prefill.neededNurses);
	const [location, setLocation] = useState<string>("");
	const [shiftNo, setShiftNo] = useState<number>(0);
	// DEFAULT: the picker owns the times — it writes the prefill (or the role's template) on mount. For an unscoped post
	// these seeds are the classic screen's own placeholders, never posted: the picker replaces them before the first paint.
	const [shiftStart, setShiftStart] = useState<DateTime>(() => prefill?.times.startTime ?? DateTime.now());
	const [shiftEnd, setShiftEnd] = useState<DateTime>(() => prefill?.times.endTime ?? DateTime.now());
	// The page's Date row moves the form to another day (APP-886). The times are absolute instants, so they move with it
	// — same clock times, the new day — or the post would land on the day the form was opened on. Adjusted during render,
	// React's pattern for state that follows a prop, so no frame shows the old day's times.
	const [timesDay, setTimesDay] = useState<DateTime>(date.startOf("day"));
	const [prefillWindow, setPrefillWindow] = useState<GapsPostTimes | undefined>(prefill?.times);
	if (!date.startOf("day").equals(timesDay)) {
		const days = Math.round(date.startOf("day").diff(timesDay, "days").days);
		setTimesDay(date.startOf("day"));
		setShiftStart((start) => start.plus({ days }));
		setShiftEnd((end) => end.plus({ days }));
		setPrefillWindow((held) => held && { startTime: held.startTime.plus({ days }), endTime: held.endTime.plus({ days }) });
	}
	const [incentive, setIncentive] = useState<GapsPostIncentive>(GapsPostIncentive.None);
	const [bonusText, setBonusText] = useState<string>("");
	const [chargeOnly, setChargeOnly] = useState<boolean>(false);

	// As the classic post screen: a role group stands for its members, each role once.
	const selectedRoles = useMemo<NurseFullRole[]>(
		() => getRolesOfRoleGroups(roleGroups, chosenDepartment.role_groups),
		[roleGroups, chosenDepartment.role_groups],
	);
	const { bonus, isBonusValid } = postIncentiveOf(incentive, bonusText);
	const hasSelection = postsByJob ? jobId !== "" : selectedRoles.length > 0;
	const areTimesValid = isOpenShiftDurationAllowed(shiftStart, shiftEnd);
	const initialTimes: GapsPostTimes | undefined = prefill?.times;

	return {
		/** The group the form was prefilled from, read once; `null` for a post started from nothing. */
		prefill,
		/** Every field is complete and valid — whether the post may go, before the write's own in-flight and scope gates. */
		isComplete: hasSelection && neededNurses >= POST_QUANTITY_MIN && areTimesValid && isBonusValid,
		fields: {
			postsByJob,
			jobId,
			setJobId,
			roleGroups,
			setRoleGroups,
			selectedRoles,
			neededNurses,
			setNeededNurses,
			location,
			setLocation,
			shiftNo,
			setShiftNo,
			shiftStart,
			setShiftStart,
			shiftEnd,
			setShiftEnd,
			initialTimes,
			isOnPrefilledGroup: isStillOnPrefill(prefill, prefillWindow, { postsByJob, jobId, initialJobId, roleGroups, shiftStart, shiftEnd }),
			incentive,
			setIncentive,
			bonusText,
			setBonusText,
			bonus,
			isBonusValid,
			chargeOnly,
			setChargeOnly,
		},
	};
};

/** The post sheet's fields and their setters, as the sheet draws and edits them. */
export type GapsPostShiftFormFields = ReturnType<typeof useGapsPostShiftForm>["fields"];
